/* Search-only relevance exception over Shopify's global available-first ordering. */
(() => {
  if (window.HoyaSearchRanking) return;

  const cache = new Map();
  const normalize = (name) => String(name || '').normalize('NFC').trim().replace(/\s+/gu, ' ').toLowerCase();
  const RESULTS_PER_PAGE = 48; // The paginate size of every search section.
  const TITLE_WORDS_MAX = 8;
  const LIST_SELECTOR = '[data-hoya-infinite-list]';

  async function resolve(url, { signal } = {}) {
    const sort = url.searchParams.get('sort_by');
    const types = url.searchParams.get('type')?.split(',');
    const query = normalize(url.searchParams.get('q'));
    if (!query || (sort && sort !== 'relevance') || (types && !types.includes('product')) ||
        url.searchParams.get('options[unavailable_products]') === 'hide' ||
        url.searchParams.get('resources[options][unavailable_products]') === 'hide') {
      return { mode: null, exactIds: [] };
    }

    const probe = new URL(`${window.Shopify.routes.root}search`, url.origin);
    for (const [name, value] of url.searchParams) {
      if (name !== 'page' && name !== 'section_id' && !name.startsWith('resources[')) {
        probe.searchParams.append(name, value);
      }
    }
    probe.searchParams.set('options[unavailable_products]', 'show');
    probe.searchParams.set('section_id', 'hoya-search-ranking');
    probe.searchParams.sort();
    const key = probe.toString();
    const cached = cache.get(key);
    if (cached && Date.now() - cached.time < 60000) return cached.result;

    const response = await fetch(key, { signal });
    if (!response.ok) throw new Error(`Search ranking request failed: ${response.status}`);
    const document = new DOMParser().parseFromString(await response.text(), 'text/html');
    const data = JSON.parse(document.querySelector('[data-hoya-search-ranking-data]').textContent);
    const exactProducts = data.products.filter((product) => normalize(product.title) === query);
    const exactIds = exactProducts.map((product) => String(product.id));
    const result = { mode: exactIds.length ? 'show' : 'last', exactIds, exactTitle: exactProducts[0]?.title, count: data.count };
    cache.delete(key);
    cache.set(key, { time: Date.now(), result });
    if (cache.size > 30) cache.delete(cache.keys().next().value);
    return result;
  }

  // The native API's ten-product cap can omit an exact match found by ordinary search.
  function containsExact(document, exactIds) {
    return Array.from(document.querySelectorAll('[data-hoya-search-product-id]'))
      .some((card) => exactIds.includes(card.dataset.hoyaSearchProductId));
  }

  function promote(document, exactIds) {
    const list = document.querySelector('.predictive-search__products');
    if (!list || !exactIds.length) return;
    const children = Array.from(list.children);
    list.replaceChildren(...children.filter((card) => exactIds.includes(card.dataset.hoyaSearchProductId)),
      ...children.filter((card) => !exactIds.includes(card.dataset.hoyaSearchProductId)));
  }

  async function completePredictive(document, url, ranking, { signal, fetcher = fetch } = {}) {
    if (ranking.exactIds.length && !containsExact(document, ranking.exactIds)) {
      try {
        const fallbackUrl = new URL(`${window.Shopify.routes.root}search`, url.origin);
        fallbackUrl.searchParams.set('q', ranking.exactTitle || url.searchParams.get('q'));
        fallbackUrl.searchParams.set('section_id', 'predictive-search');
        fallbackUrl.searchParams.set('options[unavailable_products]', 'show');
        const response = await fetcher(fallbackUrl.toString(), { signal });
        if (!response.ok) throw new Error(`Search fallback request failed: ${response.status}`);
        const fallback = new DOMParser().parseFromString(await response.text(), 'text/html');
        const products = document.querySelector('.predictive-search__products');
        const fallbackProducts = fallback.querySelector('.predictive-search__products');
        if (products && fallbackProducts && containsExact(fallback, ranking.exactIds)) {
          const limit = Number(products.style.getPropertyValue('--product-list-items-per-row')) || 5;
          products.replaceChildren(...document.importNode(fallbackProducts, true).children);
          while (products.children.length > limit) products.lastElementChild.remove();
        }
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        // A supplementary request must not discard working native results.
      }
    }
    promote(document, ranking.exactIds);
  }

  /*
   * Title-first search (owner, 21.10.2026: a search for "עציץ שקוף" shows the transparent pot first, not 30 products
   * that only mention the words). Every search page puts the products whose title holds the query's words first
   * (snippets/hoya-search-product-results.liquid). When the results run over more than one page, the title matches
   * of the later pages are brought forward too: the same search restricted to titles ("title:עציץ title:שקוף", every
   * word must be in the title) loads before the next pages, with the shopper's filters and stock order.
   */
  function titleQuery(terms) {
    const words = normalize(terms).replace(/title:/gu, ' ').split(/[^\p{L}\p{M}\p{N}]+/u)
      .filter((word) => word && !['and', 'or', 'not'].includes(word));
    return [...new Set(words)].slice(0, TITLE_WORDS_MAX).map((word) => `title:${word}`).join(' ');
  }

  function titleLeadUrl(url) {
    const sort = url.searchParams.get('sort_by');
    const types = url.searchParams.get('type')?.split(',');
    const query = titleQuery(url.searchParams.get('q'));
    if (!query || (sort && sort !== 'relevance') || (types && !types.includes('product')) ||
        Number(url.searchParams.get('page') || 1) > 1) {
      return null;
    }

    const lead = searchUrl(url);
    lead.searchParams.set('q', query);
    lead.searchParams.set('type', 'product');
    return lead;
  }

  // The ordinary search of a search or suggestion request: its query, filters and stock order.
  function searchUrl(url) {
    const search = new URL(`${window.Shopify.routes.root}search`, url.origin);
    for (const [name, value] of url.searchParams) {
      if (!['page', 'section_id', 'type'].includes(name) && !name.startsWith('resources[')) {
        search.searchParams.append(name, value);
      }
    }
    const nativeMode = url.searchParams.get('resources[options][unavailable_products]');
    if (nativeMode && !search.searchParams.has('options[unavailable_products]')) {
      search.searchParams.set('options[unavailable_products]', nativeMode);
    }
    return search;
  }

  async function fetchTitleLead(leadUrl, sectionId, { signal, fetcher = fetch } = {}) {
    const requestUrl = new URL(leadUrl);
    requestUrl.searchParams.set('section_id', sectionId);
    const response = await fetcher(requestUrl.toString(), { signal });
    if (!response.ok) throw new Error(`Title search request failed: ${response.status}`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  // Same keys as assets/hoya-infinite-scroll.js, which later de-duplicates the next pages against these items.
  const keyOf = (item) => {
    const link = item.matches('a[href]') ? item : item.querySelector('a[href]');
    return item.dataset.hoyaInfiniteKey || item.getAttribute('handle') || link?.getAttribute('href') || null;
  };
  const itemsOf = (list) => Array.from(list.children).filter((item) => item.matches(list.dataset.hoyaInfiniteItems || '*'));

  /*
   * The title matches of the list already shown keep their place and the other title matches follow them, so nothing
   * the shopper already sees jumps. When the title matches run over one page, the list's other items wait in a
   * template ("#held" in the loader's queue) until the loader has loaded the rest of them.
   */
  function composeTitleLead(root, loader, leadDocument) {
    const leadList = leadDocument.querySelector(LIST_SELECTOR);
    const list = leadList && Array.from(root.querySelectorAll(LIST_SELECTOR))
      .find((candidate) => candidate.dataset.hoyaInfiniteList === leadList.dataset.hoyaInfiniteList);
    if (!list) return false;

    const own = itemsOf(list);
    const ownKeys = new Set(own.map(keyOf));
    const leadItems = itemsOf(leadList);
    const leadKeys = new Set(leadItems.map(keyOf));
    const first = own.filter((item) => leadKeys.has(keyOf(item)));
    const rest = own.filter((item) => !leadKeys.has(keyOf(item)));
    const extras = leadItems.filter((item) => !ownKeys.has(keyOf(item))).map((item) => {
      const imported = list.ownerDocument.importNode(item, true);
      imported.removeAttribute('reveal-on-scroll'); // The theme reveals the first cards once, before these arrive.
      imported.querySelectorAll('img:not([loading])').forEach((image) => image.setAttribute('loading', 'lazy'));
      return imported;
    });
    const nextLeadUrl = leadDocument.querySelector('hoya-infinite-scroll')?.getAttribute('next-url');
    const order = nextLeadUrl ? [...first, ...extras] : [...first, ...extras, ...rest];

    if (nextLeadUrl) {
      const held = list.ownerDocument.createElement('template');
      held.dataset.hoyaInfiniteHeld = list.dataset.hoyaInfiniteList;
      held.content.append(...rest);
      list.after(held);
      const then = [];
      try { then.push(...JSON.parse(loader.getAttribute('then-urls') || '[]')); } catch (error) { /* None queued. */ }
      loader.setAttribute('then-urls', JSON.stringify(['#held', loader.getAttribute('next-url'), ...then].filter(Boolean)));
      loader.setAttribute('next-url', nextLeadUrl);
    }

    // Only what is out of place moves.
    let reference = itemsOf(list)[0] || null;
    for (const item of order) {
      if (item === reference) {
        reference = itemsOf(list)[itemsOf(list).indexOf(reference) + 1] || null;
      } else {
        list.insertBefore(item, reference);
      }
    }

    if (extras.length || nextLeadUrl || first.some((item, index) => own[index] !== item)) {
      loader.setAttribute('composed', '');
    }
    return true;
  }

  // Header search drawer: with more than one page of results, the title matches start loading with the drawer itself.
  function drawerTitleLead(url, ranking, { signal, fetcher = fetch } = {}) {
    const leadUrl = url.pathname.endsWith('/suggest') || !(ranking?.count > RESULTS_PER_PAGE) ? null : titleLeadUrl(url);
    if (!leadUrl) return null;
    const request = fetchTitleLead(leadUrl, 'hoya-search-drawer', { signal, fetcher });
    request.catch(() => {}); // Handled by prepareDrawer.
    return request;
  }

  /*
   * Header search drawer (sections/predictive-search.liquid): its loader goes on with the ordinary search of the same
   * query, filters and stock order (sections/hoya-search-drawer.liquid), and "lead" brings the title matches of every
   * page first. Runs on the fetched drawer before it is shown, so nothing moves on screen.
   */
  async function prepareDrawer(document, url, { signal, fetcher = fetch, lead = null } = {}) {
    const loader = document.querySelector('hoya-infinite-scroll');
    if (!loader) return;

    try {
      const next = searchUrl(url);
      next.searchParams.set('type', 'product');
      next.searchParams.set('page', '1');
      loader.setAttribute('next-url', next.pathname + next.search);

      const leadUrl = loader.hasAttribute('lead') ? titleLeadUrl(url) : null;
      if (leadUrl) {
        composeTitleLead(document, loader, await (lead || fetchTitleLead(leadUrl, loader.getAttribute('section-id'), { signal, fetcher })));
      }
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      // Without the title matches the drawer keeps the order of the ordinary search.
    } finally {
      loader.removeAttribute('lead');
    }
  }

  window.HoyaSearchRanking = {
    resolve, containsExact, promote, completePredictive,
    titleQuery, titleLeadUrl, fetchTitleLead, composeTitleLead, drawerTitleLead, prepareDrawer
  };

  class HoyaSearchRanking extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      this.update().catch(() => {}); // Keep the working server-rendered search on a failed/aborted probe.
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    async update() {
      // Main search: the loader waits ("lead") until the title matches of the later pages are in place.
      const capturedUrl = location.href;
      const signal = this.abortController.signal;
      const loader = this.querySelector('hoya-infinite-scroll[lead]');
      const leadUrl = loader ? titleLeadUrl(new URL(capturedUrl)) : null;
      const lead = leadUrl ? fetchTitleLead(leadUrl, this.getAttribute('section-id'), { signal }) : null;
      lead?.catch(() => {}); // Awaited below unless the section is rendered again.
      let rerendering = false;

      try {
        rerendering = await this.rank(capturedUrl);
        if (!rerendering && lead && this.isConnected && !signal.aborted && location.href === capturedUrl) {
          composeTitleLead(this, loader, await lead);
        }
      } finally {
        if (!rerendering) loader?.removeAttribute('lead');
      }
    }

    // Returns true when the section is rendered again in the stock order the query needs.
    async rank(capturedUrl) {
      const url = new URL(capturedUrl);
      const forms = Array.from(document.querySelectorAll('facets-form'))
        .filter((form) => form.getAttribute('section-id') === this.getAttribute('section-id'));
      const modeInputs = [...this.querySelectorAll('input[name="options[unavailable_products]"]'),
        ...forms.flatMap((form) => Array.from(form.querySelectorAll('input[name="options[unavailable_products]"]')))];
      modeInputs.forEach((input) => { input.value = url.searchParams.get('options[unavailable_products]') || 'last'; });
      const ranking = await resolve(url, { signal: this.abortController.signal });
      if (!this.isConnected || this.abortController.signal.aborted || location.href !== capturedUrl || !ranking.mode) return false;

      this.dataset.mode = ranking.mode;
      modeInputs.forEach((input) => { input.value = ranking.mode; });

      // Drawers move to body while open. Replacing their section then would detach the filter submission.
      const dialogs = [...forms.map((form) => form.closest('facets-drawer')),
        ...Array.from(this.querySelectorAll('[aria-controls]'), (control) => document.getElementById(control.getAttribute('aria-controls')))];
      const openDialog = dialogs.find((dialog) => dialog?.matches('facets-drawer, facets-sort-popover') &&
        (dialog.hasAttribute('open') || dialog.parentElement === document.body));
      if (openDialog) {
        await new Promise((done) => {
          const finish = () => {
            openDialog.removeEventListener('dialafter-hide', finish);
            this.abortController.signal.removeEventListener('abort', finish);
            done();
          };
          openDialog.addEventListener('dialafter-hide', finish, { once: true });
          this.abortController.signal.addEventListener('abort', finish, { once: true });
        });
        if (!this.isConnected || this.abortController.signal.aborted || location.href !== capturedUrl) return false;
      }
      if (ranking.exactIds.length) {
        const list = this.querySelector('product-list');
        if (list) {
          const cards = Array.from(list.children);
          const isExact = (card) => normalize(card.querySelector('.product-title')?.textContent) === normalize(url.searchParams.get('q'));
          list.replaceChildren(...cards.filter(isExact), ...cards.filter((card) => !isExact(card)));
        }
      }
      if ((url.searchParams.get('options[unavailable_products]') || 'last') === ranking.mode) return false;

      url.searchParams.set('options[unavailable_products]', ranking.mode);
      url.searchParams.set('section_id', this.getAttribute('section-id'));
      this.dispatchEvent(new CustomEvent('facet:update', { bubbles: true, detail: { url } }));
      return true;
    }
  }

  customElements.define('hoya-search-ranking', HoyaSearchRanking);
})();
